import Seo from "@/components/site/Seo";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const included = [
  "Dedicated senior design team, 5 hrs every business day",
  "Big-project budget for rebrands, launches & sites",
  "Never expires — use it whenever your moment comes",
  "6 live sessions/year with world design leaders",
  "2 full-day flagship masterclasses",
  "Quarterly brand audits, delivered as roadmaps",
  "Annual member summit — location to be announced",
  "Private community of fast-scaling, non-competing brands",
  "Member library — every session, framework, archived",
  "Member rates — 15% off any work beyond your budget",
];

const faqs = [
  ["Why annual only?", "Meaningful creative work — brands, sites, campaigns, systems — compounds. A monthly ticket rents a queue. A membership builds a relationship. We built for the second."],
  ["Who is this for?", "Digital-native companies with real creative budgets — B2B SaaS, e-commerce, fintech, marketplaces, media. SMBs through enterprise. If your brand is a growth lever, you're the right fit."],
  ["What counts as a big project?", "Rebrands, marketing sites, product UI/UX, launch campaigns, major identity work. Anything event-triggered rather than daily."],
  ["What if we don't use the big-project budget in a year?", "It rolls over. Never expires while your membership is active. Use it whenever your moment comes."],
  ["Where is the annual summit?", "Location is announced ahead of each cohort. It's a members-only, closed-door two days each year — keynotes, brand showcases, and one-on-one office hours with visiting leaders."],
  ["How do we apply?", "A short application, then an intro call. Reviews are weekly. Seats are limited so we can protect the room."],
];

export default function Membership() {
  return (
    <div data-testid="membership-page">
      <Seo title="Membership — A Membership, Not a Subscription | Midcreatives" description="One annual membership. A dedicated senior design team, sessions with world design leaders, quarterly brand audits, and a private network of the fastest-scaling brands." path="/membership" />
      {/* Hero */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pt-10 md:pt-16 pb-16 md:pb-24 relative">
        <div className="glow-blue" />
        <p className="font-mono-mc mb-6 relative">Membership · Cohort 2026</p>
        <h1 className="font-display-tight text-5xl sm:text-7xl lg:text-[112px] ink leading-[0.92] max-w-6xl relative">
          One membership. <span className="ink-muted">Every creative capability.</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl ink-muted max-w-3xl leading-relaxed relative">
          Membership is our engagement model — not our business. We're a global creative design
          company. This is simply the deepest, most productive way to work with us.
        </p>
      </section>

      {/* Pricing Panel */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-5 rounded-[32px] overflow-hidden border border-white/10">
          <div className="md:col-span-2 bg-[color:var(--surface-2)] p-8 md:p-12 flex flex-col justify-between min-h-[480px]">
            <div>
              <p className="font-mono-mc mb-6">The Midcreatives Membership</p>
              <h2 className="font-display-tight text-5xl md:text-6xl ink leading-[0.96]">
                One strategic partner. <span className="ink-muted">Every touchpoint covered.</span>
              </h2>
              <p className="ink-muted mt-6 leading-relaxed max-w-md">
                End-to-end creative — brand, campaigns, product, web — held under one senior team,
                for one annual commitment. By application, limited seats.
              </p>
            </div>
            <div className="mt-8">
              <p className="font-mono-mc mb-3">Terms</p>
              <ul className="ink-muted space-y-2 text-sm md:text-base">
                <li>— Annual only, billed at start of engagement</li>
                <li>— Application-based, reviewed weekly</li>
                <li>— Limited seats per cohort</li>
                <li>— 15% member rate on anything beyond your budget</li>
              </ul>
            </div>
          </div>

          <div className="md:col-span-3 bg-cream-panel p-8 md:p-12 flex flex-col justify-between">
            <div>
              <h3 className="font-display-tight text-4xl md:text-5xl text-[#0a0a0a]">
                Included in your membership:
              </h3>
              <ul className="mt-8 divide-y divide-black/10">
                {included.map((it) => (
                  <li key={it} className="flex items-center justify-between py-3 md:py-4 text-sm md:text-base text-[#0a0a0a]">
                    <span>{it}</span>
                    <Check size={18} className="shrink-0 opacity-80" />
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/apply" data-testid="membership-apply-cta" className="w-full justify-center mt-10 inline-flex items-center gap-2 px-6 py-4 rounded-full bg-[#0a0a0a] text-white font-medium hover:bg-[#1a1a1a] transition-colors">
              Get Membership <span className="arrow-slide">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* How the year runs — calendar */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-8">
            <p className="font-mono-mc mb-4">How the year runs</p>
            <h2 className="font-display-tight text-4xl md:text-6xl lg:text-7xl ink leading-[1.0]">
              A calendar of <span className="ink-muted">craft, community and compounding.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex md:items-end">
            <p className="ink-muted leading-relaxed">
              Every business day your team ships. Every quarter we audit. Every two months a
              leader session. Twice a year a full-day masterclass. Once a year, the summit.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            { month: "FEB", year: "2026", type: "Leader Session", title: "Identity systems that survive scale", where: "Virtual" },
            { month: "APR", year: "2026", type: "Masterclass", title: "Brand building at scale — full-day", where: "Virtual" },
            { month: "MAY", year: "2026", type: "Leader Session", title: "The order stories are told in", where: "Virtual" },
            { month: "JUL", year: "2026", type: "Leader Session", title: "Voice & defensible tone", where: "Virtual" },
            { month: "SEP", year: "2026", type: "Masterclass", title: "Conversion-driven design — full-day", where: "Virtual" },
            { month: "NOV", year: "2026", type: "Annual Summit", title: "Two closed-door days · Keynotes, showcases, office hours", where: "Venue TBA", featured: true },
          ].map((e, i) => (
            <div
              key={i}
              className={`rounded-3xl p-6 md:p-8 h-full min-h-[260px] flex flex-col justify-between transition-all duration-500 ${
                e.featured
                  ? "bg-cream-panel text-[#0a0a0a] hover:-translate-y-1"
                  : "bg-[color:var(--surface)] border border-white/10 hover:border-white/25"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`font-mono-mc ${e.featured ? "text-[#0a0a0a]/50" : ""}`}>{e.year}</p>
                  <p className={`font-display-tight text-6xl md:text-7xl leading-none mt-2 ${e.featured ? "text-[#0a0a0a]" : "ink"}`}>
                    {e.month}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                    e.featured
                      ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                      : "border-white/15 ink-muted"
                  }`}
                >
                  {e.type}
                </span>
              </div>
              <div>
                <h4 className={`font-display-tight text-xl md:text-2xl leading-tight ${e.featured ? "text-[#0a0a0a]" : "ink"}`}>
                  {e.title}
                </h4>
                <p className={`font-mono-mc mt-3 ${e.featured ? "text-[#0a0a0a]/60" : ""}`}>
                  {e.where}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="font-mono-mc mb-4">Questions</p>
            <h2 className="font-display-tight text-4xl md:text-5xl ink leading-[1.0]">
              Ask before you <span className="ink-muted">apply.</span>
            </h2>
          </div>
          <div className="md:col-span-8">
            <div className="border-t border-white/10">
              {faqs.map(([q, a]) => (
                <div key={q} className="border-b border-white/10 py-6 md:py-8 grid grid-cols-1 md:grid-cols-12 gap-4">
                  <h4 className="md:col-span-5 font-display-tight text-2xl ink">{q}</h4>
                  <p className="md:col-span-7 ink-muted leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="rounded-[32px] border border-white/10 bg-[color:var(--surface)] p-10 md:p-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-end relative overflow-hidden">
          <div className="glow-lime" />
          <div className="md:col-span-8 relative">
            <p className="font-mono-mc mb-6">Cohort 2026</p>
            <h2 className="font-display-tight text-4xl md:text-7xl ink leading-[1.0]">
              Seats are limited so the <span className="text-lime">room stays right.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right relative">
            <Link to="/apply" data-testid="membership-bottom-cta" className="btn-lime">
              Get Membership <span className="arrow-slide">→</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
